import React, { useEffect, useMemo, useRef, useState } from "react";
import { ClipLoader } from "react-spinners";
import regleCalculService from "../../../services/regleCalculService";
import ReglesCalculSidebar from "./ReglesCalculSidebar";
import ReglesCalculSearch from "./ReglesCalculSearch";
import ChampSection from "./ChampSection";
import { humanize } from "./humanize";
import "./ReglesCalcul.scss";

const normalize = (s) =>
  (s || "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");

const champMatches = (champKey, champ, term) => {
  if (!term) return true;
  const haystacks = [
    champKey,
    humanize(champKey),
    champ.champ,
    ...(champ.regles || []).flatMap((r) => [
      r.formule || "",
      r.description || "",
      ...(r.requis || []),
    ]),
  ];
  return haystacks.some((h) => normalize(h).includes(term));
};

const ReglesCalcul = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [activeId, setActiveId] = useState(null);
  const contentRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 150);
    return () => clearTimeout(t);
  }, [search]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = await regleCalculService.getReglesCalcul();
      setData(payload);
    } catch (e) {
      setError(
        e?.response?.data?.message ||
          e?.message ||
          "Impossible de charger les règles de calcul.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    const term = normalize(debouncedSearch.trim());
    return Object.entries(data).map(([categorieKey, champs]) => {
      const entries = Object.entries(champs || {});
      const visibles = entries.filter(([champKey, champ]) =>
        champMatches(champKey, champ, term),
      );
      return { key: categorieKey, champs: visibles };
    });
  }, [data, debouncedSearch]);

  const sidebarCategories = useMemo(
    () =>
      filtered.map(({ key, champs }) => ({
        key,
        champs: champs.map(([champKey]) => champKey),
      })),
    [filtered],
  );

  const champsCount = useMemo(
    () => filtered.reduce((acc, c) => acc + c.champs.length, 0),
    [filtered],
  );
  const reglesCount = useMemo(
    () =>
      filtered.reduce(
        (acc, c) =>
          acc +
          c.champs.reduce(
            (sub, [, champ]) => sub + (champ.regles?.length || 0),
            0,
          ),
        0,
      ),
    [filtered],
  );

  useEffect(() => {
    if (!contentRef.current) return;
    const sections = contentRef.current.querySelectorAll(
      ".regles-calcul__champ",
    );
    if (sections.length === 0) {
      setActiveId(null);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) {
          setActiveId(visible[0].target.id);
        }
      },
      {
        rootMargin: "-80px 0px -60% 0px",
        threshold: [0, 0.25, 0.5, 1],
      },
    );

    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, [filtered]);

  const handleSidebarClick = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  if (loading) {
    return (
      <div className="regles-calcul regles-calcul--centered">
        <ClipLoader />
        <p className="mt-3 text-muted">Chargement des règles de calcul…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="regles-calcul regles-calcul--centered">
        <div className="text-center">
          <i className="fas fa-exclamation-triangle fa-3x text-warning mb-3" />
          <h3>Une erreur est survenue</h3>
          <p className="text-muted">{error}</p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={fetchData}
          >
            <i className="fas fa-redo mr-2" />
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  const hasAnyChamp = champsCount > 0;
  const hasSearch = search.trim().length > 0;

  return (
    <div className="regles-calcul">
      <header className="regles-calcul__page-header">
        <div>
          <h1 className="regles-calcul__title">Règles de calcul</h1>
          <p className="regles-calcul__subtitle">
            Documentation des règles appliquées par le moteur de calcul pour
            les projections et la vente d'occasion.
          </p>
        </div>
        <span className="badge badge-dark regles-calcul__access-badge">
          <i className="fas fa-lock mr-1" />
          Accès super admin
        </span>
      </header>

      <ReglesCalculSearch
        value={search}
        onChange={setSearch}
        champsCount={champsCount}
        reglesCount={reglesCount}
      />

      <div className="regles-calcul__layout">
        <aside className="regles-calcul__sidebar-wrapper">
          <ReglesCalculSidebar
            categories={sidebarCategories}
            activeId={activeId}
            onItemClick={handleSidebarClick}
          />
        </aside>

        <main className="regles-calcul__content" ref={contentRef}>
          {!hasAnyChamp && hasSearch && (
            <div className="regles-calcul__empty">
              <i className="fas fa-search fa-2x text-muted mb-3" />
              <p>
                Aucune règle ne correspond à « <strong>{search}</strong> ».
              </p>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setSearch("")}
              >
                Effacer la recherche
              </button>
            </div>
          )}

          {!hasAnyChamp && !hasSearch && (
            <div className="regles-calcul__empty">
              <p>Aucune règle disponible.</p>
            </div>
          )}

          {filtered.map(({ key: categorieKey, champs }) => {
            if (champs.length === 0) return null;
            return (
              <section
                key={categorieKey}
                className={`regles-calcul__categorie regles-calcul__categorie--${categorieKey}`}
              >
                <h2 className="regles-calcul__categorie-title">
                  {humanize(categorieKey)}
                </h2>
                <div className="regles-calcul__categorie-content">
                  {champs.map(([champKey, champ]) => (
                    <ChampSection
                      key={champKey}
                      categorie={categorieKey}
                      champKey={champKey}
                      champ={champ}
                    />
                  ))}
                </div>
              </section>
            );
          })}
        </main>
      </div>
    </div>
  );
};

export default ReglesCalcul;
